import React, { useState } from 'react';
import { User, Mail, Lock, CreditCard, FileText, Shield, LogOut, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import BillingPage from './BillingPage';
import TermsAndConditions from './TermsAndConditions';
import PrivacyPolicy from './PrivacyPolicy';

const SettingsPage = ({ onBack }) => {
  const { logout, currentUser, resetPassword } = useAuth();
  const { tr } = useLanguage();
  const [currentView, setCurrentView] = useState('settings');

  const handleSignOut = async () => {
    try {
      await logout();
      // User will be automatically redirected to login by AuthWrapper
    } catch (error) {
      console.error('Error signing out:', error);
      alert(tr('Failed to sign out. Please try again.', 'Échec de la déconnexion. Veuillez réessayer.'));
    }
  };
  const handleBillingClick = () => {
    setCurrentView('billing');
  };

  const handleTermsClick = () => {
    setCurrentView('terms');
  };

  const handlePrivacyClick = () => {
    setCurrentView('privacy');
  };

  const handleBackToSettings = () => {
    setCurrentView('settings');
  };

  const handleResetPassword = async () => {
    if (!currentUser?.email) {
      alert(tr('No email found for your account', 'Aucun email trouvé pour votre compte'));
      return;
    }

    console.log('Sending reset password email to:', currentUser.email);
    try {
      await resetPassword(currentUser.email);
      console.log('Reset password email sent successfully');
      alert(tr('Password reset email sent successfully! Please check your email.', 'Email de réinitialisation envoyé ! Vérifiez votre boîte mail.'));
    } catch (error) {
      console.error('Reset password error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      alert(`Failed to send password reset email: ${error.message || error.code || 'Unknown error'}`);
    }
  };

  // Show BillingPage if that view is selected
  if (currentView === 'billing') {
    return <BillingPage onBack={handleBackToSettings} />;
  }

  // Show TermsAndConditions if that view is selected
  if (currentView === 'terms') {
    return <TermsAndConditions onBack={handleBackToSettings} />;
  }

  // Show PrivacyPolicy if that view is selected
  if (currentView === 'privacy') {
    return <PrivacyPolicy onBack={handleBackToSettings} />;
  }

  return (
    <div className="h-full bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100 p-4 overflow-y-auto">
      <div className="max-w-md mx-auto h-full">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden h-full flex flex-col">
          {/* Header */}
          <div className="bg-white p-6 flex-shrink-0">
            <div className="flex items-center gap-3 mb-6">
              <button 
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-bold text-gray-800">{tr('Account Settings', 'Paramètres du compte')}</h1>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {/* Account Settings Section */}
            <div className="space-y-4 mb-8">
              {/* Email */}
              <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50">
                <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">{tr('Email', 'Email')}</h3>
                  <p className="text-gray-500">{currentUser?.email || tr('No email available', 'Aucun email disponible')}</p>
                </div>
              </div>

              {/* Reset password */}
              <button 
                onClick={handleResetPassword}
                className="w-full flex items-center justify-between p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                    <Lock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{tr('Reset password', 'Réinitialiser le mot de passe')}</h3>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Billing */}
              <button 
                onClick={handleBillingClick}
                className="flex items-center justify-between p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors w-full"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{tr('Billing', 'Facturation')}</h3>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* About Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">{tr('About', 'À propos')}</h2>
              
              <div className="space-y-4">
                {/* Terms and conditions */}
                <button 
                  onClick={handleTermsClick}
                  className="w-full flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                      <FileText className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{tr('Terms and conditions', 'Conditions générales')}</h3>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Privacy policy */}
                <button 
                  onClick={handlePrivacyClick}
                  className="w-full flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{tr('Privacy policy', 'Politique de confidentialité')}</h3>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;